import { useState, useEffect } from "react";
import api from "../../services/api";

function fmtDec(n) { return (n || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }

export default function ValuationTab({ currentCompanyId }) {
  const [data, setData]         = useState(null);
  const [summary, setSummary]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [expanded, setExpanded] = useState(new Set());
  const [search, setSearch]     = useState("");
  const [catFilter, setCatFilter] = useState("");

  useEffect(() => {
    if (!currentCompanyId) return;
    setLoading(true);
    Promise.all([
      api.stocks.valuation(currentCompanyId),
      api.stocks.valuationSum(currentCompanyId),
    ]).then(([val, sum]) => { setData(val); setSummary(sum); })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [currentCompanyId]);

  if (loading) return <div style={{ textAlign: "center", padding: 60, color: "var(--text-muted)" }}>Calcul FIFO en cours…</div>;
  if (!data) return null;

  const { products = [], summary: s = {} } = data;
  const cats = [...new Set(products.map(p => p.category))].sort();

  const filtered = products.filter(p =>
    (!search || p.name.toLowerCase().includes(search.toLowerCase()) || p.sku.toLowerCase().includes(search.toLowerCase())) &&
    (!catFilter || p.category === catFilter) &&
    p.stock_current > 0
  );

  const toggleExpand = (id) => {
    const next = new Set(expanded);
    if (next.has(id)) next.delete(id); else next.add(id);
    setExpanded(next);
  };

  const maxVal = Math.max(...(summary.map(c => c.fifo_value) || [1]), 1);

  return (
    <div>
      {/* KPIs */}
      <div className="kpi-grid k4" style={{ marginBottom: 20 }}>
        <div className="kpi">
          <div className="kpi-label">Valeur FIFO totale</div>
          <div className="kpi-value" style={{ color: "var(--g)" }}>{fmtDec(s.totalFifo)} €</div>
          <div className="kpi-sub">Coût d'acquisition FIFO</div>
        </div>
        <div className="kpi">
          <div className="kpi-label">Valeur vente potentielle</div>
          <div className="kpi-value">{fmtDec(s.totalSell)} €</div>
          <div className="kpi-sub">Au prix de vente catalogue</div>
        </div>
        <div className="kpi">
          <div className="kpi-label">Marge potentielle</div>
          <div className="kpi-value" style={{ color: s.potentialMargin >= 0 ? "var(--g)" : "var(--red)" }}>
            {fmtDec(s.potentialMargin)} €
          </div>
          <div className="kpi-sub">{s.totalFifo > 0 ? `${((s.potentialMargin / s.totalFifo) * 100).toFixed(1)}% du coût FIFO` : "—"}</div>
        </div>
        <div className="kpi">
          <div className="kpi-label">Produits valorisés</div>
          <div className="kpi-value">{products.filter(p => p.stock_current > 0).length}</div>
          <div className="kpi-sub">{s.productCount} produits au total</div>
        </div>
      </div>

      <div className="g2" style={{ marginBottom: 20 }}>
        {/* Par catégorie */}
        <div className="card">
          <div style={{ fontWeight: 700, marginBottom: 12 }}>Valeur par catégorie</div>
          {summary.map(cat => (
            <div key={cat.category} style={{ marginBottom: 10 }}>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, marginBottom: 4 }}>
                <span style={{ fontWeight: 600 }}>{cat.category}</span>
                <span style={{ fontWeight: 700, color: "var(--g)" }}>{fmtDec(cat.fifo_value)} €</span>
              </div>
              <div style={{ height: 8, background: "#f1f5f9", borderRadius: 4, overflow: "hidden" }}>
                <div style={{ width: `${(cat.fifo_value / maxVal) * 100}%`, height: "100%", background: "var(--g)", borderRadius: 4, transition: "width 0.3s" }} />
              </div>
              <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 2 }}>{cat.product_count} produit(s)</div>
            </div>
          ))}
        </div>

        {/* Méthode FIFO info */}
        <div className="card" style={{ background: "linear-gradient(135deg, #1a9e7212, #185fa512)" }}>
          <div style={{ fontWeight: 700, marginBottom: 10 }}>Méthode FIFO</div>
          <div style={{ fontSize: 13, color: "var(--text-muted)", lineHeight: 1.7 }}>
            <strong>First In, First Out</strong> — les lots les plus anciens sont valorisés et consommés en premier.
            <br /><br />
            Chaque ligne du tableau affiche le coût unitaire FIFO pondéré des lots disponibles.
            Cliquez sur ▶ pour voir le détail lot par lot.
          </div>
        </div>
      </div>

      {/* Filtres */}
      <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
        <input className="form-control" placeholder="🔍 Filtrer…" style={{ flex: 1, maxWidth: 300 }} value={search} onChange={e => setSearch(e.target.value)} />
        <select className="form-control" style={{ width: 160 }} value={catFilter} onChange={e => setCatFilter(e.target.value)}>
          <option value="">Toutes catégories</option>
          {cats.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      {/* Tableau FIFO */}
      <div className="card">
        <div className="tbl-overflow">
          <table className="tbl">
            <thead>
              <tr><th></th><th>SKU</th><th>Produit</th><th>Catégorie</th><th>Stock</th><th>Coût FIFO/u</th><th>Valeur FIFO</th><th>PV HT</th><th>Marge/u</th></tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr><td colSpan={9} style={{ textAlign: "center", padding: 24, color: "var(--text-muted)" }}>Aucun produit en stock</td></tr>
              ) : filtered.map(p => {
                const margin = p.selling_price - p.fifo_unit_cost;
                const marginPct = p.fifo_unit_cost > 0 ? (margin / p.fifo_unit_cost) * 100 : 0;
                const isOpen = expanded.has(p.id);
                return (
                  <>
                    <tr key={p.id} style={{ cursor: p.batches?.length > 0 ? "pointer" : "default" }}
                      onClick={() => p.batches?.length > 0 && toggleExpand(p.id)}>
                      <td style={{ width: 28, textAlign: "center", color: "var(--text-muted)" }}>
                        {p.batches?.length > 0 ? (isOpen ? "▼" : "▶") : "·"}
                      </td>
                      <td style={{ fontFamily: "monospace", fontSize: 12, color: "var(--g)" }}>{p.sku}</td>
                      <td style={{ fontWeight: 600 }}>{p.name}{p.brand && <span style={{ fontWeight: 400, color: "var(--text-muted)", fontSize: 11 }}> · {p.brand}</span>}</td>
                      <td><span className="badge info">{p.category}</span></td>
                      <td style={{ fontWeight: 700 }}>{p.stock_current} {p.unit}</td>
                      <td style={{ fontWeight: 700 }}>{fmtDec(p.fifo_unit_cost)} €</td>
                      <td style={{ fontWeight: 700, color: "var(--g)" }}>{fmtDec(p.fifo_value)} €</td>
                      <td>{fmtDec(p.selling_price)} €</td>
                      <td style={{ color: margin >= 0 ? "var(--g)" : "var(--red)", fontWeight: 600 }}>
                        {fmtDec(margin)} € ({marginPct.toFixed(0)}%)
                      </td>
                    </tr>
                    {isOpen && p.batches?.map((b, bi) => (
                      <tr key={`b-${b.id}`} style={{ background: "#f9fafb", fontSize: 12 }}>
                        <td></td>
                        <td colSpan={2} style={{ paddingLeft: 32, color: "var(--text-muted)" }}>
                          Lot #{bi + 1} · {b.batch_number || "—"}
                          {b.expiry_date && <span style={{ color: "var(--red)", marginLeft: 8 }}>⏱ exp. {b.expiry_date}</span>}
                        </td>
                        <td style={{ color: "var(--text-muted)" }}>{b.purchase_date}</td>
                        <td style={{ color: "var(--amber)" }}>{b.quantity_remaining} / {b.quantity_initial} {p.unit}</td>
                        <td style={{ fontWeight: 600 }}>{fmtDec(b.unit_price)} €</td>
                        <td style={{ fontWeight: 700, color: "var(--g)" }}>{fmtDec(b.quantity_remaining * b.unit_price)} €</td>
                        <td colSpan={2}></td>
                      </tr>
                    ))}
                  </>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
